import * as lodash from 'lodash';

import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { AppMenus } from '@configs/app.menus';
import { MODULES } from '@configs/modules.enum';

import { MenuItem } from '@models/view/menu.model';

import { PermissionService, UserStore } from '@cxc/core/services';

import { isStaffSuperAdmin } from '@utils/staff-super-admin';

export const moduleGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const userStore = inject(UserStore);
  const router = inject(Router);
  const permissionService = inject(PermissionService);

  const user = userStore.signal();

  if (user?.modules?.length === 0) {
    router.navigate(['/home']);
    return false;
  }

  const splitUrl = state.url.split('/');
  splitUrl.splice(0, 1);

  const moduleFromUrl = splitUrl[1] as MODULES;
  const modulePath =
    splitUrl.length >= 2 ? `/${splitUrl[0]}/${splitUrl[1]}` : undefined;
  const submodulePath =
    splitUrl.length >= 3
      ? `/${splitUrl[0]}/${splitUrl[1]}/${splitUrl[2]}`
      : undefined;
  let menu: MenuItem | undefined = undefined;

  // LEN-2764: Admin is only for the SUPER_ADMIN of CXC, at any depth (its child
  // routes carry no permission strings, so the checks below would let anyone in)
  if (
    modulePath === AppMenus[MODULES.SHELL_ADMIN].path &&
    !isStaffSuperAdmin(user)
  ) {
    router.navigate(['/home']);
    return false;
  }

  let allowed = false;

  // Module exists in enum
  if (Object.values(MODULES).includes(moduleFromUrl)) {
    menu = AppMenus[moduleFromUrl];
  }
  // Due to constant changes, it is posible that the url
  // differs from our enum, so we check against url paths
  else {
    menu = lodash
      .chain(AppMenus)
      .find(menu => {
        if (menu.path === modulePath) {
          return true;
        }
        return false;
      })
      .value();
  }

  // Trying to access a menu that doesn't exist
  if (!menu) {
    router.navigate(['/home']);
    return false;
  }

  // This shouldn't happen, but if it does we should allow it
  if (splitUrl.length < 2) {
    allowed = true;
  }
  // Trying to access a module wihout submodules
  // Compare path with state.url to confirm access
  else if (splitUrl.length === 2 && menu.path === modulePath) {
    allowed =
      permissionService.hasModuleAccess(moduleFromUrl) &&
      permissionService.hasArrayOfPermissions(menu.requiredPermissions || []);
  }
  // Submodule access
  else if (splitUrl.length >= 3) {
    const permissions =
      lodash
        .chain(menu.subitems)
        .find(submodule => submodule.path === submodulePath)
        .get('requiredPermissions')
        .value() || [];

    allowed = permissionService.hasArrayOfPermissions(permissions);
  }

  if (allowed) {
    return true;
  }

  router.navigate(['/home']);
  return false;
};
